import React from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';
import SkipToContent from '../components/SkipToContent';
import OtherStates from '../components/OtherStates';

/**
 * California Professional License Renewal Landing Page
 */

interface ProfessionInfo {
  id: string;
  name: string;
  icon: string;
  credits: number;
  cycleYears: number;
  terminology: string;
  highlights: string[];
  stateBoard: string;
  boardUrl: string;
}

const CALicenseRenewal: React.FC = () => {
  const professions: ProfessionInfo[] = [
    {
      id: 'ARCH',
      name: 'Architect',
      icon: '🏛️',
      credits: 45,
      cycleYears: 2,
      terminology: 'LU',
      highlights: ['45 LU biennial', 'Min 5 LU in HSW (Health, Safety & Welfare)', '2 LU in Disability Access', 'Zero Net Energy Design (ZNE) topics accepted', 'Max 15 LU self-study'],
      stateBoard: 'California Architects Board',
      boardUrl: 'https://www.cab.ca.gov/'
    },
    {
      id: 'PE',
      name: 'Professional Engineer',
      icon: '⚙️',
      credits: 30,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: ['30 PDH biennial', '4 PDH Ethics (one-time, then every 6 years)', '1 PDH Traffic Control (if practicing civil/traffic)', 'General engineering subjects accepted'],
      stateBoard: 'California Board for Professional Engineers',
      boardUrl: 'https://www.bpelsg.ca.gov/'
    },
    {
      id: 'PLS',
      name: 'Professional Land Surveyor',
      icon: '📐',
      credits: 30,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: ['30 PDH biennial', '4 PDH Ethics (one-time, then every 6 years)', 'Surveying-related subjects', 'Professional competency activities'],
      stateBoard: 'California Board for Professional Engineers & Land Surveyors',
      boardUrl: 'https://www.bpelsg.ca.gov/'
    },
    {
      id: 'CPA',
      name: 'Certified Public Accountant',
      icon: '💼',
      credits: 80,
      cycleYears: 2,
      terminology: 'CPE',
      highlights: ['80 CPE hours biennial', 'Min 40 hours Technical Subjects', 'Min 20 hours per year', '4 hours Ethics every 2 years', '2 hours Regulatory Review (every 6 years)'],
      stateBoard: 'California Board of Accountancy',
      boardUrl: 'https://www.cba.ca.gov/'
    },
    {
      id: 'RN',
      name: 'Registered Nurse',
      icon: '⚕️',
      credits: 30,
      cycleYears: 2,
      terminology: 'contact hours',
      highlights: ['30 contact hours biennial', '1 hour Implicit Bias Training (one-time for licenses after 2023)', 'Must be relevant to nursing practice', 'First renewal within 2 years of NCLEX exempt'],
      stateBoard: 'California Board of Registered Nursing',
      boardUrl: 'https://www.rn.ca.gov/'
    },
    {
      id: 'APRN',
      name: 'Nurse Practitioner / APRN',
      icon: '🩺',
      credits: 30,
      cycleYears: 2,
      terminology: 'contact hours',
      highlights: ['30 contact hours biennial', '3 hours Schedule II Controlled Substances (if prescribing)', '1 hour Implicit Bias Training (one-time)', '6 hours Geriatrics (if treating 65+)'],
      stateBoard: 'California Board of Registered Nursing',
      boardUrl: 'https://www.rn.ca.gov/'
    },
    {
      id: 'LCSW',
      name: 'Licensed Clinical Social Worker',
      icon: '🧠',
      credits: 36,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['36 hours biennial', '6 hours Law and Ethics', '6 hours Suicide Risk Assessment', '3 hours Telehealth', '7 hours HIV/AIDS', '7 hours Child Abuse Assessment'],
      stateBoard: 'California Board of Behavioral Sciences',
      boardUrl: 'https://www.bbs.ca.gov/'
    },
    {
      id: 'LMFT',
      name: 'Licensed Marriage & Family Therapist',
      icon: '👨‍👩‍👧‍👦',
      credits: 36,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['36 hours biennial', '6 hours Law and Ethics', '6 hours Suicide Risk Assessment', '3 hours Telehealth', '7 hours Spousal/Partner Abuse', '7 hours Child Abuse Assessment'],
      stateBoard: 'California Board of Behavioral Sciences',
      boardUrl: 'https://www.bbs.ca.gov/'
    },
    {
      id: 'LPCC',
      name: 'Licensed Professional Clinical Counselor',
      icon: '🤝',
      credits: 36,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['36 hours biennial', '6 hours Law and Ethics', '6 hours Suicide Risk Assessment', '3 hours Telehealth', '15 hours LPCC-specific topics', '10 hours Addiction/Treatment'],
      stateBoard: 'California Board of Behavioral Sciences',
      boardUrl: 'https://www.bbs.ca.gov/'
    },
    {
      id: 'RE',
      name: 'Real Estate Broker/Salesperson',
      icon: '🏠',
      credits: 45,
      cycleYears: 4,
      terminology: 'hours',
      highlights: ['45 hours every 4 years', '18 hours mandatory topics (Ethics, Agency, Fair Housing, etc.)', '27 hours elective topics', 'First renewal: different requirements'],
      stateBoard: 'California Department of Real Estate',
      boardUrl: 'https://www.dre.ca.gov/'
    },
    {
      id: 'APPR',
      name: 'Real Estate Appraiser',
      icon: '📊',
      credits: 28,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['28 hours biennial (Certified General/Residential)', '21 hours biennial (Licensed)', '7-hour National USPAP Update (mandatory)', 'Must use AQB-approved providers'],
      stateBoard: 'California Bureau of Real Estate Appraisers',
      boardUrl: 'https://www.brea.ca.gov/'
    },
    {
      id: 'LAW',
      name: 'Attorney',
      icon: '⚖️',
      credits: 25,
      cycleYears: 3,
      terminology: 'MCLE',
      highlights: ['25 MCLE credits triennial', 'Min 4 hours Legal Ethics', 'Min 1 hour Elimination of Bias', '1 hour Competence Issues (first compliance period)', 'Max 12 hours self-study'],
      stateBoard: 'State Bar of California - MCLE',
      boardUrl: 'http://www.calbar.ca.gov/Attorneys/MCLE-CLE'
    }
  ];

  const structuredData = [
    // Main WebPage schema
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "California Professional License Renewal Requirements",
      "description": "Complete guide to California professional license renewal requirements for Architects, Engineers, CPAs, Nurses, Social Workers, and more.",
      "url": "https://cectrack.com/ca-license-renewal",
      "about": {
        "@type": "Thing",
        "name": "Professional License Renewal",
        "sameAs": [
          "https://www.dca.ca.gov/",
          "https://www.cab.ca.gov/",
          "https://www.bpelsg.ca.gov/"
        ]
      },
      "publisher": {
        "@type": "Organization",
        "name": "CEC Track",
        "url": "https://cectrack.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://cectrack.com/og-image.png"
        }
      },
      "mainEntity": {
        "@type": "ItemList",
        "name": "California Professional Licenses",
        "numberOfItems": 12,
        "itemListElement": professions.map((prof, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Course",
            "name": prof.name,
            "description": `${prof.name} license renewal requires ${prof.credits} ${prof.terminology} every ${prof.cycleYears} year(s)`,
            "provider": {
              "@type": "Organization",
              "name": prof.stateBoard,
              "url": prof.boardUrl
            }
          }
        }))
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://cectrack.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "California License Renewal",
            "item": "https://cectrack.com/ca-license-renewal"
          }
        ]
      }
    },
    // FAQPage schema
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How many CE hours do I need to renew my California professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "CE requirements vary by profession in California. Architects need 45 LU biennial, Engineers need 30 PDH biennial, CPAs need 80 CPE hours biennial, Nurses need 30 contact hours biennial, Social Workers need 36 hours biennial, and Attorneys need 25 MCLE credits triennial."
          }
        },
        {
          "@type": "Question",
          "name": "Which state agency handles professional license renewal in California?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "California professional licenses are overseen by the Department of Consumer Affairs (DCA), which includes boards for Architects, Engineers, CPAs, Nurses, Social Workers, and other professions. Attorneys are regulated by the State Bar of California."
          }
        },
        {
          "@type": "Question",
          "name": "How often do I need to renew my California professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most California professions renew biennially (every 2 years), including Architects, Engineers, CPAs, Nurses, and Social Workers. Real Estate Brokers/Salespersons renew every 4 years, and Attorneys renew every 3 years."
          }
        },
        {
          "@type": "Question",
          "name": "Does California accept online CE courses?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, California accepts online continuing education courses from approved providers. However, most professions have limits on self-study or online hours. For example, Architects can use a maximum of 15 LU from self-study, and Attorneys can use a maximum of 12 MCLE hours from self-study."
          }
        }
      ]
    },
    // GovernmentOrganization schemas
    {
      "@context": "https://schema.org",
      "@type": "GovernmentOrganization",
      "name": "California Department of Consumer Affairs",
      "url": "https://www.dca.ca.gov/",
      "areaServed": "California",
      "description": "Oversees professional licensing boards in California including Architects, Engineers, CPAs, Nurses, and Social Workers"
    },
    {
      "@context": "https://schema.org",
      "@type": "GovernmentOrganization",
      "name": "State Bar of California",
      "url": "http://www.calbar.ca.gov/",
      "areaServed": "California",
      "description": "Regulates attorneys and MCLE requirements in California"
    }
  ];

  return (
    <>
      <SkipToContent />
      <SEOHelmet
        title="California Professional License Renewal | CE Requirements & Deadlines"
        description="Complete guide to California professional license renewal requirements. Track CE credits for Architects, Engineers, CPAs, Nurses, Social Workers, and more."
        keywords="California license renewal, CA CE requirements, CA continuing education, California professional licenses, CA DCA, California nurses, CA CPAs, CA engineers"
        canonical="https://cectrack.com/ca-license-renewal"
        ogImage="https://cectrack.com/og-image.png"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section 
        aria-label="Page header"
        style={{
          background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
          color: 'white',
          padding: '80px 0 60px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.4
          }}
        />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '1.5rem', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                California Professional License Renewal
              </h1>
              <p style={{ fontSize: '1.3rem', marginBottom: '2rem', opacity: 0.95 }}>
                Never miss a deadline. Track your continuing education requirements across all your California professional licenses in one place.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link 
                  to="/register" 
                  className="btn btn-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                >
                  Start Tracking Free
                </Link>
                <a 
                  href="#professions" 
                  className="btn btn-outline-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px' }}
                >
                  View Requirements
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section aria-label="Statistics" style={{ padding: '60px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row g-4 text-center">
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#d97706', marginBottom: '0.5rem' }} aria-label="12 professions">12</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Professions Supported</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#d97706', marginBottom: '0.5rem' }} aria-label="Over 60 categories">60+</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Category Requirements</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#d97706', marginBottom: '0.5rem' }} aria-label="100 percent compliant">100%</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>DCA Compliant</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#d97706', marginBottom: '0.5rem' }} aria-label="24/7 access">24/7</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Access Anywhere</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professions Grid */}
      <main id="main-content">
        <section id="professions" aria-labelledby="professions-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 id="professions-heading" style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
                California CE Requirements by Profession
              </h2>
              <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
                Select your profession to see detailed continuing education requirements
              </p>
            </div>

          <div className="row g-4">
            {professions.map((prof) => (
              <div key={prof.id} className="col-md-6 col-lg-4">
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ 
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{prof.icon}</div>
                    <h5 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1rem' }}>
                      {prof.name}
                    </h5>
                    
                    <div 
                      style={{
                        backgroundColor: '#fef3c7',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginBottom: '1rem'
                      }}
                    >
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#d97706' }}>
                        {prof.credits} {prof.terminology}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                        {prof.cycleYears === 1 ? 'Annual' : prof.cycleYears === 2 ? 'Biennial' : prof.cycleYears === 3 ? 'Triennial' : `Every ${prof.cycleYears} Years`}
                      </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6c757d', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                        Key Requirements:
                      </div>
                      <ul style={{ fontSize: '0.9rem', margin: 0, paddingLeft: '1.2rem' }}>
                        {prof.highlights.map((highlight, idx) => (
                          <li key={idx} style={{ marginBottom: '0.3rem' }}>{highlight}</li>
                        ))}
                      </ul>
                    </div>

                    <div 
                      style={{
                        paddingTop: '1rem',
                        borderTop: '1px solid #e9ecef',
                        fontSize: '0.85rem',
                        color: '#6c757d'
                      }}
                    >
                      <strong>{prof.stateBoard}</strong>
                      <br />
                      <a 
                        href={prof.boardUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#f59e0b', textDecoration: 'none' }}
                        onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                      >
                        Visit State Board →
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose CEC Track */}
      <section style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
                Simplify Your California License Management
              </h2>
              <p style={{ fontSize: '1.1rem', color: '#6c757d', marginBottom: '2rem' }}>
                Managing multiple California professional licenses doesn't have to be complicated. 
                CEC Track automatically tracks your requirements, calculates your progress, and sends 
                timely reminders so you never miss a renewal deadline.
              </p>
              <ul style={{ fontSize: '1.1rem', listStyle: 'none', padding: 0 }}>
                {[
                  'Automated deadline tracking for all CA professions',
                  'Upload certificates and match to requirements',
                  'Real-time progress reports by category',
                  'Multi-state support for professionals licensed in multiple states',
                  'Secure cloud storage for all documents',
                  'Email notifications for upcoming deadlines'
                ].map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: '1rem', paddingLeft: '2rem', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#10b981', fontSize: '1.5rem' }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-lg-6">
              <div 
                className="p-5 text-center"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📱</div>
                <h3 style={{ fontSize: '1.8rem', fontWeight: '700', marginBottom: '1rem' }}>
                  Start Tracking Today
                </h3>
                <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
                  Join hundreds of California professionals who trust CEC Track to manage their continuing education.
                </p>
                <Link 
                  to="/register" 
                  className="btn btn-primary btn-lg px-5 py-3"
                  style={{ 
                    fontWeight: '600',
                    background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
                    border: 'none',
                    borderRadius: '8px'
                  }}
                >
                  Create Free Account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* State Resources */}
      <section aria-labelledby="resources-heading" style={{ padding: '80px 0' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 id="resources-heading" style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
              California State Resources
            </h2>
            <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
              Official resources and contact information for California professional licensing
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-6">
              <article className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    California Department of Consumer Affairs (DCA)
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    The DCA oversees most professional licensing boards in California including architects, 
                    engineers, accountants, nurses, and behavioral health professionals.
                  </p>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Phone:</strong> <a href="tel:+18009527210" aria-label="Call DCA at 800-952-7210">(800) 952-7210</a>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Website:</strong>{' '}
                    <a href="https://www.dca.ca.gov/" target="_blank" rel="noopener noreferrer" aria-label="Visit DCA website">
                      www.dca.ca.gov
                    </a>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Office:</strong> 1625 North Market Blvd., Suite N-112, Sacramento, CA 95834
                  </div>
                  <a 
                    href="https://www.dca.ca.gov/about_us/boards.shtml" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                    aria-label="View all DCA boards"
                  >
                    View All Boards & Bureaus
                  </a>
                </div>
              </article>
            </div>

            <div className="col-md-6">
              <article className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    State Bar of California
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    Regulates the practice of law in California and administers the Minimum Continuing Legal Education (MCLE) program for attorneys.
                  </p>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Phone:</strong> <a href="tel:+14156387800" aria-label="Call State Bar at 415-638-7800">(415) 638-7800</a>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Website:</strong>{' '}
                    <a href="http://www.calbar.ca.gov/" target="_blank" rel="noopener noreferrer" aria-label="Visit State Bar website">
                      www.calbar.ca.gov
                    </a>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Office:</strong> 180 Howard Street, San Francisco, CA 94105
                  </div>
                  <a 
                    href="http://www.calbar.ca.gov/Attorneys/MCLE-CLE" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                    aria-label="Visit MCLE information"
                  >
                    MCLE Information
                  </a>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* Other States Cross-Linking */}
      <OtherStates currentState="CA" />

      {/* CTA Section */}
      <section 
        aria-label="Call to action"
        style={{
          background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
          color: 'white',
          padding: '80px 0'
        }}
      >
        <div className="container text-center">
          <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
            Ready to Simplify Your California License Renewal?
          </h2>
          <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.95 }}>
            Join professionals across California who never worry about missing CE requirements
          </p>
          <Link 
            to="/register" 
            className="btn btn-light btn-lg px-5 py-3"
            style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)' }}
          >
            Get Started Free
          </Link>
        </div>
      </section>
      </main>
    </>
  );
};

export default CALicenseRenewal;
